import { createSelector, createSlice } from "@reduxjs/toolkit";
import {
  getCoursesBySkillPathId,
  getUsersJobCourses,
} from "../../services/courseService";
import { selectDomainSkillPathsBySkillPathLevel } from "./skillPathReducer";
import { userConfigurationActions } from "./userConfigurationReducer";
import { STATUS } from "../../constants/statuses";
import { launchPadLayoutActions } from "./layoutReducer";
// import {
//   getJobCoursesWithSkills,
// } from "../../redux/reducers/domainReducer";

const courseSlice = createSlice({
  name: "courses",
  //our data format will be an array of courses with a property skillPathInfo
  // which is also an array of objects that contain the course's specficis for
  //each skill path
  initialState: [],
  reducers: {
    removeUserCourses(state) {
      const coursesAffected = state.filter((course) =>
        course.isInUserJob
      );

      coursesAffected.forEach((course) => {
        course.isInUserJob = false;
      });
    },
    removeCoursesSkillPathInfo(state, action) {
      const skillPathIdsToRemove = action.payload;
      const coursesAffected = state.filter((course) =>
        Object.keys(course.skillPathInfo).some((x) =>
          skillPathIdsToRemove.includes(x)
        )
      );

      coursesAffected.forEach((course) => {
        skillPathIdsToRemove.forEach((skillPathId) => {
          if (course.skillPathInfo[skillPathId]) {
            delete course.skillPathInfo[skillPathId];
          }
        });
      });
    },
    addCourses(state, action) {
      state.push(...action.payload);
    },
    updateCourses(state, action) {
      action.payload.forEach((id) => {
        let stateCourse = state.find((y) => y.id === id);
        if (stateCourse) {
          stateCourse.status = STATUS.IN_PROGRESS;
        }
      });
    },
    completeCourses(state, action) {
      action.payload.forEach((id) => {
        let stateCourse = state.find((y) => y.id === id);
        if (stateCourse) {
          stateCourse.status = STATUS.COMPLETED;
        }
      });
    },
    unlockCourses(state, action) {
      action.payload.forEach((courseToUnlock) => {
        let stateCourse = state.find((y) => y.id === courseToUnlock.courseId);
        if (
          stateCourse &&
          stateCourse.skillPathInfo &&
          stateCourse.skillPathInfo[courseToUnlock.skillPathId]
        ) {
          stateCourse.skillPathInfo[courseToUnlock.skillPathId].locked = false;
        }
      });
    },
    upsertCourses(state, action) {
      action.payload.forEach((course) => {
        //we search each course by content id in the state
        //if we find the course we just add the skill path specifics for that course
        //which are rank ,if it is required and if it is locked.
        //The course status is common accross skill paths based on the bussiness requirements
        let courseSkillPathSpecificInfo = {
          rank: course.rank,
          required: course.required,
          locked: course.locked,
        };
        let stateCourse = state.find((y) => y.id === course.id);
        if (stateCourse) {
          stateCourse.status = course.status ?? stateCourse.status;
          stateCourse.isInUserJob =
            course.isInUserJob ?? stateCourse.isInUserJob;
          //add skill path info only if it is available from api
          if (course.skillPathId) {
            stateCourse.skillPathInfo[course.skillPathId] =
              courseSkillPathSpecificInfo;
          }
          stateCourse.skills =
            stateCourse.skills.length === 0
              ? course.contentSkills ?? []
              : stateCourse.skills;
        } else {
          state.push({
            id: course.id,
            code: course.code,
            name: course.name,
            summary: course.summary,
            description: course.description,
            duration: course.duration,
            thumbnail: course.thumbnail,
            link: course.link,
            status: course.status,
            isInUserJob: course.isInUserJob,
            skillPathInfo: {
              ...(course.skillPathId && {
                [course.skillPathId]: courseSkillPathSpecificInfo,
              }),
            },
            skills: course.contentSkills ?? [],
          });
        }
      });
    },
  },
});

export const courseActions = courseSlice.actions;

const unlockCourses = (completedCourses) => {
  return (dispatch, getState) => {
    const { courses } = getState();
    let coursesToUnlock = [];
    let stateCompletedCourses = courses.filter((x) =>
      completedCourses.includes(x.id)
    );

    stateCompletedCourses.forEach((completedCourse) => {
      if (completedCourse.skillPathInfo) {
        Object.keys(completedCourse.skillPathInfo).forEach((skillPathId) => {
          //for each completed course that is cached we iterate through each the skill paths
          //where it is required to find the next course and then if all previous(based on rank) are completed then we unlock it
          if (completedCourse.skillPathInfo[skillPathId].required) {
            let allSkillPathCourses = courses.filter(
              (x) =>
                x.skillPathInfo &&
                x.skillPathInfo[skillPathId] &&
                x.skillPathInfo[skillPathId].required
            );
            let nextCourse = allSkillPathCourses.find(
              (x) =>
                x.skillPathInfo[skillPathId].rank ===
                completedCourse.skillPathInfo[skillPathId].rank + 1
            );
            let allPrevious = allSkillPathCourses.filter(
              (x) =>
                x.skillPathInfo[skillPathId].rank <
                completedCourse.skillPathInfo[skillPathId].rank
            );
            if (
              nextCourse &&
              allPrevious.every((x) => x.status === STATUS.COMPLETED)
            ) {
              coursesToUnlock.push({
                courseId: nextCourse.id,
                skillPathId: skillPathId,
              });
            }
          }
        });
      }
    });

    dispatch(courseActions.unlockCourses(coursesToUnlock));
  };
};

export const completeCourses = (completedCourses) => {
  return (dispatch) => {
    dispatch(courseActions.completeCourses(completedCourses));
    dispatch(unlockCourses(completedCourses));
  };
};

export const setCompletionModalInfo = (completedCoursesInfo) => {
  return (dispatch) => {
    // Add new completed courses in an array to show the completion modal for each one
    dispatch(
      launchPadLayoutActions.setPendingCompletedCourses(completedCoursesInfo)
    );
  };
};

export const getCoursesBySkillPath = (id) => {
  return async (dispatch, getState) => {
    const { courses } = getState();
    let skillPathCourses = courses.filter((course) => course.skillPathInfo[id]);

    if (skillPathCourses.length === 0) {
      let courses = await getCoursesBySkillPathId(id);
      dispatch(courseActions.upsertCourses(courses.data));
    }
  };
};

export const getUserJobRoleCourses = () => {
  return async (dispatch, getState) => {
    const { courses } = getState();
    let userJobCourses = courses.filter((course) => course.isInUserJob);

    if (userJobCourses.length === 0) {
      let courses = await getUsersJobCourses();
      dispatch(
        userConfigurationActions.setState({
          jobRole: courses?.data[0]?.jobRole,
          jobRoleId: courses?.data[0]?.domainId,
        })
      );
      dispatch(
        courseActions.upsertCourses(
          courses.data.map((x) => ({ ...x, isInUserJob: true }))
        )
      );
      // dispatch(getJobCoursesWithSkills(courses?.data[0]?.domainId));
    }
  };
};

//----------------------------------------------------- Selectors
export const selectAllCourses = (state) => state.courses;

export const selectSkillPathCourses = createSelector(
  [selectAllCourses, (state, skillPathId) => skillPathId],
  (courses, skillPathId) => {
    return courses
      .filter((course) => course.skillPathInfo[skillPathId])
      .map((x) => {
        //for each course we lookup into the courses skillPathInfo object
        //to find the actual course info for the specific skill path exists
        let courseSkillPathInfo = x.skillPathInfo[skillPathId];
        return {
          id: x.id,
          name: x.name,
          summary: x.summary,
          description: x.description,
          duration: x.duration,
          code: x.code,
          thumbnail: x.thumbnail,
          link: x.link,
          status: x.status,
          rank: courseSkillPathInfo.rank,
          skillPathId: skillPathId,
          locked: courseSkillPathInfo.locked,
          required: courseSkillPathInfo.required,
        };
      });
  }
);

export const selectSkillPathRequiredCourses = createSelector(
  [selectAllCourses, selectSkillPathCourses],
  (skillPathCourses) => {
    return skillPathCourses
      .filter((x) => x.required)
      .sort((a, b) => a.rank - b.rank);
  }
);

export const selectSkillPathOptionalCourses = createSelector(
  [selectAllCourses, selectSkillPathCourses],
  (skillPathCourses) => {
    return skillPathCourses
      .filter((x) => !x.required)
      .sort((a, b) => a.rank - b.rank);
  }
);

export const selectCoursesBySkillPathLevelId = createSelector(
  [
    selectAllCourses,
    selectDomainSkillPathsBySkillPathLevel,
    (state, domainId, skillPathLevelId, selectedSkills) => selectedSkills,
  ],
  (courses, domainSkillPaths, selectedSkills) => {
    return courses
      .filter((x) => domainSkillPaths.some((dsp) => x.skillPathInfo[dsp.id]))
      .filter(
        (x) =>
          selectedSkills.length === 0 ||
          selectedSkills.some((ss) => x.skills.some((y) => y.id === ss))
      )
      .map((x) => {
        //for each course we lookup into the courses skillPathInfo object
        //to find the actual course info for the specific skill path exists
        let skillPathId = domainSkillPaths[0]?.id;
        let courseSkillPathInfo = x.skillPathInfo[skillPathId];
        return {
          id: x.id,
          name: x.name,
          summary: x.summary,
          description: x.description,
          duration: x.duration,
          code: x.code,
          thumbnail: x.thumbnail,
          link: x.link,
          status: x.status,
          skills: x.skills.slice().sort((a, b) => a.rank - b.rank),
          rank: courseSkillPathInfo.rank,
          skillPathId: skillPathId,
          locked: courseSkillPathInfo.locked,
          required: courseSkillPathInfo.required,
        };
      });
  }
);

export const selectUserJobCourses = createSelector(
  [selectAllCourses],
  (courses) => {
    return courses.filter((x) => x.isInUserJob);
  }
);

export default courseSlice.reducer;
